<!-- The side panel's contents for the one module being edited. The deck renders a single instance
     in its right-hand rail and swaps the block it is given, so there is no popover to position and
     nothing that closes because a click landed somewhere unexpected. -->
<div class="panel-head">
  <div class="d-flex align-items-center">
    <h2 class="panel-title mb-0">Edit module</h2>
    <button type="button"
            class="panel-close btn btn-sm p-0 ml-auto"
            aria-label="Close module settings"
            (click)="closed.emit()">
      <i class="fas fa-close" aria-hidden="true"></i>
    </button>
  </div>
  <div class="d-flex align-items-center mt-2 mb-1">
    <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'type-' + block.id">Module</label>
    <!-- Every module with an example of each, so choosing one is not a guess from its name. -->
    <button type="button" class="module-guide-btn ml-auto" (click)="openGuide()">
      <i class="fas fa-book-open mr-1" aria-hidden="true"></i>Module guide
    </button>
  </div>
  <or-report-select [id]="'type-' + block.id"
                    [groups]="moduleOptions"
                    [value]="block.reportType"
                    (selectedChange)="typeChange.emit($any($event))">
  </or-report-select>
  <p class="grey4 nav-font12 mt-1 mb-0">{{ guidance[block.reportType] }}</p>
</div>

<!-- The same four groups, in the same order, for every module: what it reads, how its figures are
     worked out, how it is laid out, and what its parts are called. A group with nothing in it for
     this module is left out, and a closed one says what it is set to. -->
<div class="panel-body">
  <section class="config-group">
    <button type="button"
            class="config-group-head"
            [attr.aria-expanded]="isOpen('data')"
            [attr.aria-controls]="'cfg-data-' + block.id"
            (click)="toggleGroup('data')">
      <span class="config-group-text">
        <span class="config-group-name">{{ groupName('data') }}</span>
        @if (!isOpen('data') && summaries.data) {
          <span class="config-group-summary">{{ summaries.data }}</span>
        }
      </span>
      <i class="fas fa-chevron-down config-group-chevron" [class.is-open]="isOpen('data')" aria-hidden="true"></i>
    </button>
    <div class="config-group-body" [id]="'cfg-data-' + block.id" [hidden]="!isOpen('data')">
      <!-- Methodology charts nothing, so it has figures to type instead of a question to pick. A ranked
           bar draws several questions at once and gets one row per bar; everything else picks one. -->
      @if (isImageModule) {
        <!-- The Image module's picture, uploaded here like a heatmap's and kept the same way. -->
        <p class="grey5 nav-font12 font-weight-bold mt-3 mb-1">Image</p>
        @if (images?.canUpload) {
          <input #moduleImageFile
                 type="file"
                 class="d-none"
                 accept="image/jpeg,image/png,image/gif,image/webp"
                 (change)="chooseImage($event)">
          <button type="button"
                  class="btn btn-sm btn-link px-0 shadow-none nav-font12"
                  [disabled]="imageUploading"
                  (click)="moduleImageFile.click()">
            <i class="fas fa-upload mr-1" aria-hidden="true"></i>{{ imageUploading ? 'Uploading…' : imageId ? 'Replace image' : 'Upload an image' }}
          </button>
          <p class="grey4 nav-font12 mb-0">JPEG, PNG, GIF or WebP, up to 10 MB.</p>
          @if (imageError && imageError.blockId === block.id) {
            <p class="nav-font12 text-danger mt-1 mb-0" role="alert">{{ imageError.text }}</p>
          }
        } @else {
          <p class="grey4 nav-font12 mb-0">Images can be uploaded on a report, not on a template.</p>
        }

        <label class="grey5 nav-font12 font-weight-bold mt-3 mb-1" [attr.for]="'alt-' + block.id">Description</label>
        <input type="text"
               class="form-control form-control-sm nav-font12"
               maxlength="250"
               placeholder="What the picture shows"
               [id]="'alt-' + block.id"
               [attr.aria-describedby]="'alt-hint-' + block.id"
               [value]="altText"
               (change)="patch.emit({ altText: $any($event.target).value.trim() })">
        <p class="grey4 nav-font12 mt-1 mb-0" [id]="'alt-hint-' + block.id">Read out to anyone who can't see the picture.</p>
      } @else if (chartsNothing && !isDial) {
        <p class="grey4 nav-font12 mt-3 mb-0">
          <i class="fas fa-circle-info mr-1" aria-hidden="true"></i>Its content is edited on the
          module itself.
        </p>
      } @else if (takesAxes) {
        <!-- One question per axis, labelled, because which question goes on which axis is half the
             decision. A generic list of questions answers neither half. -->
        <p class="grey5 nav-font12 font-weight-bold mt-3 mb-1">Horizontal axis</p>
        <or-report-select [ariaLabel]="'Question for the horizontal axis'"
                          [groups]="questionGroups"
                          [value]="axisKey(0)"
                          (selectedChange)="setAxis(0, $event)">
        </or-report-select>

        <p class="grey5 nav-font12 font-weight-bold mt-2 mb-1">Vertical axis</p>
        <or-report-select [ariaLabel]="'Question for the vertical axis'"
                          [groups]="questionGroups"
                          [value]="axisKey(1)"
                          (selectedChange)="setAxis(1, $event)">
        </or-report-select>

      } @else if (takesGrid) {
        <!-- A ranking is a ranking across a whole grid, so the unit picked here is the question, not
             its rows: its figures only total a ranking over all of its own attributes. The rows are
             still listed underneath, because a deck sometimes reports part of a grid. -->
        <p class="grey5 nav-font12 font-weight-bold mt-3 mb-1">Ranking question</p>
        <or-report-select [ariaLabel]="'Ranking question for ' + label"
                          [options]="gridOptions"
                          [value]="chartedGrid"
                          (selectedChange)="setGrid($event)">
        </or-report-select>

        @if (gridRows.length) {
          <div class="d-flex align-items-baseline mt-3 mb-1">
            <span class="grey5 nav-font12 font-weight-bold">Attributes shown</span>
            <span class="grey4 nav-font12 ml-auto">{{ gridRowCountLabel() }}</span>
          </div>
          <ul class="item-list list-unstyled mb-0">
            @for (row of gridRows; track row.key) {
              <li class="d-flex align-items-start">
                <input type="checkbox"
                       class="mt-1"
                       [id]="'row-' + block.id + '-' + row.key"
                       [checked]="isGridRowIncluded(row)"
                       (change)="toggleGridRow(row, $any($event.target))">
                <label class="grey5 nav-font12 mb-0 ml-2" [attr.for]="'row-' + block.id + '-' + row.key">
                  {{ row.label }}
                </label>
              </li>
            }
          </ul>
        }

      } @else if (takesPanels && itemsQuestion; as question) {
        <!-- One question's own items: a tick list rather than a row of dropdowns per item, which is
             what nine of them used to be. Unticking rewrites the block to name the ones left. -->
        <p class="grey5 nav-font12 font-weight-bold mt-3 mb-1">Question</p>
        <or-report-select [ariaLabel]="'Question for ' + label"
                          [groups]="questionGroups"
                          [value]="question.key"
                          (selectedChange)="setVariable($event)">
        </or-report-select>

        <div class="d-flex align-items-baseline mt-3 mb-1">
          <span class="grey5 nav-font12 font-weight-bold">Options shown</span>
          <span class="grey4 nav-font12 ml-auto">{{ itemCountLabel() }}</span>
        </div>
        <ul class="item-list list-unstyled mb-0">
          @for (item of itemChoices; track item.key) {
            <li class="d-flex align-items-start">
              <input type="checkbox"
                     class="mt-1"
                     [id]="'item-' + block.id + '-' + item.key"
                     [checked]="isItemIncluded(item)"
                     (change)="toggleItem(item, $any($event.target))">
              <label class="grey5 nav-font12 mb-0 ml-2" [attr.for]="'item-' + block.id + '-' + item.key">
                {{ item.label }}
              </label>
            </li>
          }
        </ul>

      } @else if (takesPanels) {
        <!-- Collapsible, and closed to begin with once the list is long: a battery's dozen rows push
             every other setting out of reach, and the count is the part that is worth seeing shut. -->
        <button type="button"
                class="config-section w-100 d-flex align-items-center mt-3 mb-1"
                [attr.aria-expanded]="questionsOpen"
                [attr.aria-controls]="'questions-' + block.id"
                (click)="toggleQuestions()">
          <span class="grey5 nav-font12 font-weight-bold">Questions</span>
          <span class="grey4 nav-font12 ml-auto mr-2">{{ questionCountLabel }}</span>
          <i class="fas grey4"
             [class.fa-chevron-up]="questionsOpen"
             [class.fa-chevron-down]="!questionsOpen"
             aria-hidden="true"></i>
        </button>
        <div [id]="'questions-' + block.id" [hidden]="!questionsOpen">
        @for (key of chartedKeys; track key; let i = $index) {
          <div class="d-flex align-items-center mb-1">
            <or-report-select class="flex-fill"
                              [ariaLabel]="'Question ' + (i + 1) + ' for ' + label"
                              [groups]="questionGroups"
                              [value]="key"
                              (selectedChange)="setPanel(i, $event)">
            </or-report-select>
            <button type="button" class="editor-action editor-remove btn btn-sm p-0 ml-1"
                    [disabled]="chartedKeys.length < 2"
                    [attr.aria-label]="'Remove ' + labelFor(key) + ' from ' + label"
                    (click)="removePanel(key)">
              <i class="fas fa-close" aria-hidden="true"></i>
            </button>
          </div>
        }
        <!-- Never holds a value: picking here adds a row above and this goes straight back to its
             placeholder, so there is nothing to reset and nothing to read as a duplicate. -->
        <or-report-select [ariaLabel]="'Add another question to ' + label"
                          [groups]="questionGroups"
                          [value]="null"
                          placeholder="+ Add a question…"
                          (selectedChange)="addPanel($event)">
        </or-report-select>
        </div>
      } @else {
        <label class="grey5 nav-font12 font-weight-bold mt-3 mb-1" [attr.for]="'q-' + block.id">{{ isCrosstab ? 'Rows' : 'Question' }}</label>
        <or-report-select [id]="'q-' + block.id"
                          [groups]="questionGroups"
                          [value]="chartedKeys[0] ?? ''"
                          placeholder="Choose a question…"
                          (selectedChange)="setVariable($event)">
        </or-report-select>
      }

      @if (takesGroupBy) {
        <label class="grey5 nav-font12 font-weight-bold mb-1" [attr.for]="'group-' + block.id">
          {{ groupByLabel }}
        </label>
        <or-report-select [id]="'group-' + block.id"
                          [options]="groupByOptions"
                          [value]="groupByKey"
                          placeholder="Choose a question…"
                          (selectedChange)="setGroupBy($event)">
        </or-report-select>

        <!-- Every answer is a panel unless it is unticked, and a second question can be added
             below when the comparison a client asked for cuts across questions -- "Female" and
             "Male" from one, "45+" from another. -->
        @if (showsSubgroups) {
          <div class="d-flex align-items-baseline mt-2 mb-1">
            <span class="grey5 nav-font12 font-weight-bold">{{ panelWord }} shown</span>
            <span class="grey4 nav-font12 ml-auto">{{ panelCountLabel() }}</span>
          </div>

          @for (group of panelGroups; track group.variableKey) {
            <div class="mb-2">
              @if (panelGroups.length > 1) {
                <div class="d-flex align-items-center mb-1">
                  <span class="grey4 nav-font12">{{ group.label }}</span>
                  @if (!group.primary) {
                    <button type="button"
                            class="editor-action editor-remove btn btn-sm p-0 ml-auto"
                            [attr.aria-label]="'Remove ' + group.label"
                            (click)="removePanelQuestion(group.variableKey)">
                      <i class="fas fa-close" aria-hidden="true"></i>
                    </button>
                  }
                </div>
              }

              @if (!group.answers.length) {
                <p class="grey4 nav-font12 mb-0">Nobody answered this question.</p>
              }
              <ul class="item-list list-unstyled mb-0">
                @for (answer of group.answers; track answer) {
                  <li class="d-flex align-items-start">
                    <input type="checkbox"
                           class="mt-1"
                           [id]="'panel-' + block.id + '-' + group.variableKey + '-' + answer"
                           [checked]="isPanelIncluded(group.variableKey, answer)"
                           (change)="togglePanel(group.variableKey, answer, $any($event.target))">
                    <label class="grey5 nav-font12 mb-0 ml-2"
                           [attr.for]="'panel-' + block.id + '-' + group.variableKey + '-' + answer">
                      {{ answer }}
                    </label>
                  </li>
                }
              </ul>
            </div>
          }

          <!-- Never holds a value: picking here adds a question above and this goes straight back
               to its placeholder, the same way the charted question list works. -->
          <or-report-select ariaLabel="Add another question"
                            [options]="addQuestionOptions"
                            [value]="null"
                            placeholder="+ Add another question…"
                            (selectedChange)="addPanelQuestion($event)">
          </or-report-select>
        }

        <div class="mt-1">
          <input type="checkbox"
                 [id]="'total-' + block.id"
                 [checked]="includeTotal"
                 (change)="patch.emit({ includeTotal: $any($event.target).checked })">
          <label class="grey4 nav-font12 mb-0 ml-1" [attr.for]="'total-' + block.id">
            Include a Total {{ panelNoun }} for all respondents
          </label>
        </div>
      }

      @if (showsTableStats) {
        <!-- A report full of tables is built from one: set this one up, then copy it down the survey.
             Every copy keeps the columns, tests and display; only the rows change. -->
        <button type="button"
                [id]="'bulk-toggle-' + block.id"
                class="bulk-toggle w-100 mt-3"
                [attr.aria-expanded]="bulkOpen"
                [attr.aria-controls]="'bulk-' + block.id"
                (click)="bulkOpen = !bulkOpen">
          <i class="fas fa-layer-group mr-2" aria-hidden="true"></i>
          <span class="flex-fill text-left">Make tables for more questions</span>
          <i class="fas fa-chevron-down config-group-chevron" [class.is-open]="bulkOpen" aria-hidden="true"></i>
        </button>
        <p class="sr-only" aria-live="polite">{{ bulkDone }}</p>
        <div class="bulk-panel" [id]="'bulk-' + block.id" [hidden]="!bulkOpen">
          <p class="grey4 nav-font12 mb-2">
            One table per question ticked, placed under this one with the same columns, tests and
            display.
          </p>
          <input type="search"
                 class="form-control form-control-sm mb-2"
                 aria-label="Search questions"
                 placeholder="Search questions…"
                 [value]="bulkQuery"
                 (input)="bulkQuery = $any($event.target).value">
          <div class="d-flex align-items-center mb-1">
            <input type="checkbox"
                   [id]="'bulk-all-' + block.id"
                   [checked]="allBulkPicked"
                   (change)="toggleAllBulk()">
            <label class="grey5 nav-font12 font-weight-bold mb-0 ml-1" [attr.for]="'bulk-all-' + block.id">
              {{ bulkQuery ? 'All matching' : 'All questions' }}
            </label>
            <span class="grey4 nav-font12 ml-auto">{{ bulkPicked.size }} ticked</span>
          </div>
          <ul class="item-list list-unstyled mb-2">
            @for (variable of bulkChoices; track variable.key) {
              <li class="d-flex align-items-start">
                <input type="checkbox"
                       class="mt-1"
                       [id]="'bulk-' + block.id + '-' + variable.key"
                       [checked]="bulkPicked.has(variable.key)"
                       (change)="toggleBulk(variable.key, $any($event.target).checked)">
                <label class="grey5 nav-font12 mb-0 ml-2" [attr.for]="'bulk-' + block.id + '-' + variable.key">
                  <span class="grey4 mr-1">{{ variable.position }}</span>{{ variable.label }}
                </label>
              </li>
            } @empty {
              <li class="grey4 nav-font12">No question matches.</li>
            }
          </ul>
          <button type="button" class="btn btn-sm btn-primary w-100" [disabled]="!bulkPicked.size" (click)="createBulk()">
            {{ bulkPicked.size ? 'Create ' + bulkPicked.size + ' table' + (bulkPicked.size === 1 ? '' : 's') : 'Tick the questions to tabulate' }}
          </button>
        </div>

        <!-- A third question, slicing the whole table. Every slice costs base the way a filter does,
             which is why the hint says so before anyone picks one. -->
        <div class="d-flex align-items-center mt-3 mb-1">
          <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'layer-' + block.id">Split by</label>
          <or-config-hint class="ml-1"
                          text="A third question whose answers slice the whole table -- region across a gender banner. Each slice is a smaller sample, so keep it to a question with few answers.">
          </or-config-hint>
        </div>
        <or-report-select [id]="'layer-' + block.id"
                          [options]="layerOptions"
                          [value]="layerBy"
                          placeholder="None"
                          (selectedChange)="setLayer($event)">
        </or-report-select>
        @if (layerBy) {
          <label class="grey5 nav-font12 font-weight-bold mt-2 mb-1" [attr.for]="'layerview-' + block.id">Show the slices as</label>
          <or-report-select [id]="'layerview-' + block.id"
                            [options]="layerViewOptions"
                            [value]="layerView"
                            (selectedChange)="patch.emit({ layerView: $any($event) })">
          </or-report-select>
        }

        <!-- This table only, on top of whatever the deck's filters select: "among product users".
             Answers of one question are alternatives; every question listed must hold. -->
        <div class="d-flex align-items-center mt-3 mb-1">
          <span class="grey5 nav-font12 font-weight-bold">Only include respondents who answered</span>
          <or-config-hint class="ml-1"
                          text="Narrows this table alone. Ticked answers of one question are alternatives; every question listed must match. Leave empty for everyone the deck shows.">
          </or-config-hint>
        </div>
        @for (group of tableFilterGroups; track group.variableKey) {
          <div class="filter-card mb-2">
            <div class="d-flex align-items-center mb-1">
              <span class="grey5 nav-font12 font-weight-bold text-truncate">{{ group.label }}</span>
              <button type="button"
                      class="editor-action editor-remove btn btn-sm p-0 ml-auto"
                      [attr.aria-label]="'Stop filtering on ' + group.label"
                      (click)="removeTableFilterQuestion(group.variableKey)">
                <i class="fas fa-close" aria-hidden="true"></i>
              </button>
            </div>
            @if (isFilterPending(group.variableKey)) {
              <p class="grey4 nav-font12 mb-1">Tick the answers to include. Nothing is filtered until you do.</p>
            }
            <div class="chip-list" role="group" [attr.aria-label]="'Answers of ' + group.label + ' to include'">
              @for (answer of group.answers; track answer) {
                <label class="answer-chip" [class.is-on]="isFilteredIn(group.variableKey, answer)">
                  <input type="checkbox"
                         class="sr-only"
                         [checked]="isFilteredIn(group.variableKey, answer)"
                         (change)="toggleTableFilter(group.variableKey, answer, $any($event.target))">
                  {{ answer }}
                </label>
              }
            </div>
          </div>
        }
        <or-report-select ariaLabel="Filter this table by a question"
                          [options]="tableFilterOptions"
                          [value]="null"
                          placeholder="+ Filter by a question…"
                          (selectedChange)="addTableFilterQuestion($event)">
        </or-report-select>
      }

      @if (takesMatrix) {
        <!-- A second battery, picked as a whole grid: the priority column reads a ranking exactly as
             the ranked driver does, and the two must show the same figure. -->
        <div class="d-flex align-items-center mt-2 mb-1">
          <label class="grey5 nav-font12 font-weight-bold mb-0">Priority column</label>
          <or-config-hint class="ml-1"
                          text="A ranking question asked about the same attributes. Its top-N share becomes the priority column.">
          </or-config-hint>
        </div>
        <or-report-select [ariaLabel]="'Ranking question for the priority column'"
                          [options]="gridOptions"
                          [value]="priorityGrid"
                          (selectedChange)="setPriorityGrid($event)">
        </or-report-select>
      }

      @if (showsHeatmap) {
        <!-- The survey's own creative is copied in with the data; an upload replaces it on this module
             only. Either way the file must be the one the survey showed, since the boxes are in its
             pixels. -->
        <div class="d-flex align-items-center mt-2 mb-1">
          <p class="grey5 nav-font12 font-weight-bold mb-0">Image</p>
          <or-config-hint class="ml-1"
                          text="The image respondents marked. The survey's own is used unless you upload one. Use the same file the survey showed: the marks are placed by its pixels, so a resized copy puts them in the wrong place.">
          </or-config-hint>
        </div>
        <p class="grey4 nav-font12 mb-1">
          @if (imageId) {
            Using an uploaded image.
          } @else if (surveyImage === 'found') {
            Using the image from the survey.
          } @else if (surveyImage === 'pending') {
            Looking for the survey's image…
          } @else {
            The survey has no image for this question.
          }
        </p>
        <!-- An upload kept from before the survey's image arrived can still be dropped, but not
             replaced: the survey's is the file the boxes were drawn on. -->
        @if (surveyImage === 'found') {
          @if (imageId) {
            <button type="button" class="btn btn-sm btn-link px-0 shadow-none nav-font12" (click)="useSurveyImage()">
              Use the survey's image
            </button>
          }
        } @else if (surveyImage === 'pending') {
          <!-- Nothing to offer until it is known whether the survey has one. -->
        } @else if (canUploadImage) {
          <!-- A real button opening a hidden file input: a styled label takes no focus ring. -->
          <input #imageFile
                 type="file"
                 class="d-none"
                 accept="image/jpeg,image/png,image/gif,image/webp"
                 (change)="chooseImage($event)">
          <div class="d-flex flex-wrap align-items-center">
            <button type="button"
                    class="btn btn-sm btn-link px-0 shadow-none nav-font12 mr-3"
                    [disabled]="imageUploading"
                    (click)="imageFile.click()">
              <i class="fas fa-upload mr-1" aria-hidden="true"></i>{{ imageUploading ? 'Uploading…' : imageId ? 'Replace image' : 'Upload an image' }}
            </button>
            @if (imageId && !imageUploading) {
              <button type="button" class="btn btn-sm btn-link px-0 shadow-none nav-font12" (click)="useSurveyImage()">
                Remove image
              </button>
            }
          </div>
          @if (imageError && imageError.blockId === block.id) {
            <p class="nav-font12 text-danger mt-1 mb-0" role="alert">{{ imageError.text }}</p>
          }
        } @else {
          <p class="grey4 nav-font12 mb-0">Images can be uploaded on a report, not on a template.</p>
        }

        <div class="mt-2">
          <input type="checkbox"
                 [id]="'optout-' + block.id"
                 [checked]="showOptOut"
                 (change)="patch.emit({ showOptOut: $any($event.target).checked })">
          <label class="grey4 nav-font12 mb-0 ml-1" [attr.for]="'optout-' + block.id">
            Show the share who chose "none of these"
          </label>
        </div>

        @if (showsScaleChoice) {
          <div class="d-flex align-items-center mt-2">
            <input type="checkbox"
                   [id]="'sharedscale-' + block.id"
                   [checked]="sharedScale"
                   (change)="patch.emit({ sharedScale: $any($event.target).checked })">
            <label class="grey4 nav-font12 mb-0 ml-1" [attr.for]="'sharedscale-' + block.id">
              Use one colour scale for every panel
            </label>
            <or-config-hint class="ml-1"
                            text="On: the same colour means the same share in every panel, so a rarer reaction looks cooler. Off: each panel's busiest spot is the hottest colour, with its own legend.">
            </or-config-hint>
          </div>
        }
      }

      @if (showsWordCloud) {
        <div class="d-flex align-items-center mt-2 mb-1">
          <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'mincount-' + block.id">
            Least respondents per word
          </label>
          <or-config-hint class="ml-1"
                          text="A word used by fewer people than this is left out, so one person's typo is never in the cloud. Blank uses 2.">
          </or-config-hint>
        </div>
        <input type="number"
               class="form-control form-control-sm w-100"
               min="1"
               [id]="'mincount-' + block.id"
               placeholder="2"
               [value]="minCount"
               (change)="setMinCount($any($event.target).value)">

        <!-- Respondents echo the question back, so its own words are the biggest in the cloud and
             say nothing. Off by default; on where the thing being asked about is the finding. -->
        <div class="mt-2">
          <input type="checkbox"
                 [id]="'questionwords-' + block.id"
                 [checked]="useQuestionWords"
                 (change)="patch.emit({ useQuestionWords: $any($event.target).checked })">
          <label class="grey4 nav-font12 mb-0 ml-1" [attr.for]="'questionwords-' + block.id">
            Include words from the question itself
          </label>
        </div>

        <div class="d-flex align-items-center mt-2 mb-1">
          <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'stopwords-' + block.id">
            Words to leave out
          </label>
          <or-config-hint class="ml-1"
                          text="Comma separated. Common English words go automatically; this is for words specific to this study.">
          </or-config-hint>
        </div>
        <input type="text"
               class="form-control form-control-sm w-100"
               [id]="'stopwords-' + block.id"
               placeholder="e.g. company, service"
               [value]="stopwords"
               (change)="setStopwords($any($event.target).value)">
      }
    </div>
  </section>

  @if (hasScoring) {
    <section class="config-group">
      <button type="button"
              class="config-group-head"
              [attr.aria-expanded]="isOpen('scoring')"
              [attr.aria-controls]="'cfg-scoring-' + block.id"
              (click)="toggleGroup('scoring')">
        <span class="config-group-text">
          <span class="config-group-name">{{ groupName('scoring') }}</span>
          @if (!isOpen('scoring') && summaries.scoring) {
            <span class="config-group-summary">{{ summaries.scoring }}</span>
          }
        </span>
        <i class="fas fa-chevron-down config-group-chevron" [class.is-open]="isOpen('scoring')" aria-hidden="true"></i>
      </button>
      <div class="config-group-body" [id]="'cfg-scoring-' + block.id" [hidden]="!isOpen('scoring')">
        <!-- Bands: shared by the dial and the bar chart, so a score and the distribution under it can
             be grouped the same way. Counted off the ends of the answer list, which is why sorting and
             Show top disappear while any are set. -->
        @if (showsBands) {
          <div class="d-flex align-items-center mt-3 mb-1">
            <label class="grey5 nav-font12 font-weight-bold mb-0">Answer bands</label>
            <or-config-hint class="ml-1"
                            text="Groups of answers counted from the top of the scale down — Promoters, Passives, Detractors. Nothing in the survey says which answers are good, so this is yours to set.">
            </or-config-hint>
          </div>

          @for (band of bands; track $index) {
            <div class="d-flex align-items-center" [class.mt-1]="!$first">
              <input type="text"
                     class="form-control form-control-sm"
                     maxlength="40"
                     [attr.aria-label]="'Name of band ' + ($index + 1)"
                     placeholder="Band name"
                     [value]="band.label"
                     (input)="setBandLabel($index, $any($event.target).value)">
              <input type="number"
                     class="form-control form-control-sm ml-1 band-size"
                     min="1"
                     [attr.aria-label]="'Answers in band ' + ($index + 1)"
                     [value]="band.options"
                     (input)="setBandSize($index, $any($event.target).value)">
              <button type="button"
                      class="btn btn-sm btn-link text-danger px-1 shadow-none"
                      [attr.aria-label]="'Remove band ' + ($index + 1)"
                      (click)="removeBand($index)">
                <i class="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <!-- The answers it actually covers, in the survey's own words. A band read from the wrong
                 end does not look wrong, it looks like a different result, so the card and this row
                 both show their working. -->
            @if (bandRange($index)) {
              <p class="grey4 nav-font10 mb-0 mt-1">{{ bandRange($index) }}</p>
            }
          }

          @if (unbandedCount) {
            <button type="button"
                    class="btn btn-sm btn-link px-0 shadow-none nav-font12"
                    (click)="addBand()">
              <i class="fas fa-plus mr-1" aria-hidden="true"></i>Add a band
              <span class="grey4">({{ unbandedCount }} left)</span>
            </button>
          } @else {
            <p class="grey4 nav-font10 mb-0 mt-1">
              Every answer is in a band. Narrow one to make room for another.
            </p>
          }

          @if (bands.length) {
            <div class="d-flex align-items-center mt-2 mb-1">
              <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'topband-' + block.id">
                Best answer is
              </label>
              <or-config-hint class="ml-1" [text]="topBandHint"></or-config-hint>
            </div>
            <or-report-select [id]="'topband-' + block.id"
                              [options]="topBandOptions"
                              [value]="topBand"
                              (selectedChange)="patch.emit({ topBand: $any($event) })">
            </or-report-select>
          }
        }

        <!-- The stage matrix's stages: answer bands under another name, and set the same way -- a
             name and a run of the answers every row shares, left to right. Answers past the last
             stage are drawn ungrouped. -->
        @if (takesStages) {
          <div class="d-flex align-items-center mt-3 mb-1">
            <span class="grey5 nav-font12 font-weight-bold">Stages</span>
            <or-config-hint class="ml-1"
                            text="Groups of neighbouring columns, left to right, each under its own heading and colour. Answers past the last stage are drawn ungrouped.">
            </or-config-hint>
          </div>

          @for (stage of stages; track $index) {
            <div class="d-flex align-items-center" [class.mt-1]="!$first">
              <input type="text"
                     class="form-control form-control-sm"
                     maxlength="30"
                     [attr.aria-label]="'Name of stage ' + ($index + 1)"
                     placeholder="Stage name"
                     [value]="stage.label"
                     (input)="setStageLabel($index, $any($event.target).value)">
              <!-- Put back to what was kept, since a width past the scale is cut down and the box
                   would otherwise go on showing the number typed. -->
              <input type="number"
                     class="form-control form-control-sm ml-1 band-size"
                     min="1"
                     [attr.aria-label]="'Answers in stage ' + ($index + 1)"
                     [value]="stage.rungs"
                     (change)="$any($event.target).value = setStageSpan($index, $any($event.target).value)">
              <button type="button"
                      class="btn btn-sm btn-link text-danger px-1 shadow-none"
                      [attr.aria-label]="'Remove stage ' + ($index + 1)"
                      (click)="removeStage($index)">
                <i class="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <p class="grey4 nav-font10 mb-0 mt-1">{{ stageRange($index) }}</p>
          }

          @if (unstagedCount) {
            <button type="button"
                    class="btn btn-sm btn-link px-0 shadow-none nav-font12"
                    (click)="addStage()">
              <i class="fas fa-plus mr-1" aria-hidden="true"></i>Add a stage
              <span class="grey4">({{ unstagedCount }} left)</span>
            </button>
          } @else {
            <p class="grey4 nav-font10 mb-0 mt-1">
              Every answer is in a stage. Narrow one to make room for another.
            </p>
          }

          <!-- Which answers the leading column adds up. Only once it has a name, since without one
               it is not drawn at all. -->
          @if (stageTotalLabel.trim()) {
            <div class="d-flex align-items-baseline mt-3 mb-1">
              <span class="grey5 nav-font12 font-weight-bold">{{ stageTotalLabel }} counts</span>
              <or-config-hint class="ml-1"
                              text="The answers the leading column adds up. Untick one to leave it out -- Unaware, for Total Aided Awareness.">
              </or-config-hint>
            </div>
            <ul class="item-list list-unstyled mb-0">
              @for (rung of stageRungs; track rung) {
                <li class="d-flex align-items-start">
                  <input type="checkbox"
                         class="mt-1"
                         [id]="'totalcounts-' + block.id + '-' + $index"
                         [checked]="countsTowardTotal(rung)"
                         (change)="setCountsTowardTotal(rung, $any($event.target).checked)">
                  <label class="grey5 nav-font12 mb-0 ml-2" [attr.for]="'totalcounts-' + block.id + '-' + $index">
                    {{ rung }}
                  </label>
                </li>
              }
            </ul>
          }
        }

        @if (isDial) {
          @if (showsBands) {
            <div class="d-flex align-items-center mt-2 mb-1">
              <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'metric-' + block.id">
                Score
              </label>
              <or-config-hint class="ml-1"
                              text="A net is the top band minus the bottom, ignoring the middle. A share is one band on its own.">
              </or-config-hint>
            </div>
            <or-report-select [id]="'metric-' + block.id"
                              [options]="metricOptions"
                              [value]="metric"
                              (selectedChange)="patch.emit({ metric: $any($event) })">
            </or-report-select>

            @if (metric === 'share' && shareBandOptions.length) {
              <label class="grey5 nav-font12 font-weight-bold mt-2 mb-1" [attr.for]="'shareband-' + block.id">
                Which band
              </label>
              <or-report-select [id]="'shareband-' + block.id"
                                [options]="shareBandOptions"
                                [value]="shareBand"
                                (selectedChange)="patch.emit({ shareBand: +$any($event) })">
              </or-report-select>
            }
          }

          @if (showsScale) {
            <div class="d-flex align-items-center mt-2 mb-1">
              <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'scale-' + block.id">
                Scale maximum
              </label>
              <or-config-hint class="ml-1"
                              text="What a full gauge means. The score and its subheading are edited on the score itself.">
              </or-config-hint>
            </div>
            <input type="number"
                   class="form-control form-control-sm w-100"
                   min="1"
                   [id]="'scale-' + block.id"
                   placeholder="100"
                   [value]="scaleMax"
                   (input)="setScaleMax($any($event.target).value)">

            <div class="d-flex align-items-center mt-2 mb-1">
              <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'scalemin-' + block.id">
                Scale minimum
              </label>
              <or-config-hint class="ml-1"
                              text="What an empty gauge means. Leave it blank unless the score can go below zero.">
              </or-config-hint>
            </div>
            <input type="number"
                   class="form-control form-control-sm w-100"
                   [id]="'scalemin-' + block.id"
                   placeholder="-100"
                   [value]="scaleMin"
                   (input)="setScaleMin($any($event.target).value)">
          }
        }

        @if (showsRankedDriver) {
          <div class="d-flex align-items-center mt-2 mb-1">
            <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'topranks-' + block.id">
              Places counted as top
            </label>
            <or-config-hint class="ml-1"
                            text="How many rank positions the bar covers. The legend and the figures follow it.">
            </or-config-hint>
          </div>
          <input type="number"
                 class="form-control form-control-sm w-100"
                 min="1"
                 [max]="topRanksMax"
                 [id]="'topranks-' + block.id"
                 [value]="topRanks"
                 (change)="setTopRanks($any($event.target).value)">

          <!-- Which end of the answer list is first place. Named rather than "first"/"last": a survey
               writes a rank scale either way round and the datamap does not record which, so read the
               wrong way this inverts the finding instead of degrading it. -->
          <div class="d-flex align-items-center mt-2 mb-1">
            <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'rankone-' + block.id">
              First place is
            </label>
            <or-config-hint class="ml-1" [text]="rankOneEndHint"></or-config-hint>
          </div>
          <or-report-select [id]="'rankone-' + block.id"
                            [options]="rankOneEndOptions"
                            [value]="rankOneEnd"
                            (selectedChange)="patch.emit({ rankOneEnd: $any($event) })">
          </or-report-select>

          <!-- A number of bands rather than a cut at a percentage, because this deck has a live filter
               rail: a threshold would move attributes between tiers every time a client filters. -->
          <div class="d-flex align-items-center mt-2 mb-1">
            <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'tiers-' + block.id">
              Priority tiers
            </label>
            <or-config-hint class="ml-1"
                            text="The sorted attributes are split into this many equal bands. Choose it from where the figures drop off.">
            </or-config-hint>
          </div>
          <or-report-select [id]="'tiers-' + block.id"
                            [options]="tierCountOptions"
                            [value]="tierCountValue"
                            (selectedChange)="setTierCount($any($event))">
          </or-report-select>

          <!-- Named beside the count that makes them, the way bands and stages are: one box per tier
               the settings produce, blank for the default name shown in it. -->
          @if (tierNames.length) {
            <div class="d-flex align-items-center mt-2 mb-1">
              <span class="grey5 nav-font12 font-weight-bold">Tier names</span>
              <or-config-hint class="ml-1"
                              text="What each tier is headed, from the top. Blank uses the name shown in the box.">
              </or-config-hint>
            </div>
            @for (tier of tierNames; track $index) {
              <input type="text"
                     class="form-control form-control-sm w-100"
                     [class.mt-1]="!$first"
                     maxlength="40"
                     [attr.aria-label]="'Name of tier ' + ($index + 1)"
                     [placeholder]="tier.placeholder"
                     [value]="tier.value"
                     (input)="setTierLabel($index, $any($event.target).value)">
            }
          }
        }

        @if (takesMatrix) {
          @if (priorityGrid) {
            <!-- The same two readings the tiered ranking offers, because the column is its figure:
                 how many places count as the top, and which end of the scale is first place. -->
            <div class="d-flex align-items-center mt-2 mb-1">
              <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'priorityranks-' + block.id">
                Places counted as top
              </label>
              <or-config-hint class="ml-1"
                              text="How many rank positions the priority column counts. Its name follows it unless you give it one.">
              </or-config-hint>
            </div>
            <input type="number"
                   class="form-control form-control-sm w-100"
                   min="1"
                   [max]="topRanksMax"
                   [id]="'priorityranks-' + block.id"
                   [value]="topRanks"
                   (change)="setTopRanks($any($event.target).value)">

            <div class="d-flex align-items-center mt-2 mb-1">
              <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'priorityone-' + block.id">
                First place is
              </label>
              <or-config-hint class="ml-1" [text]="rankOneEndHint"></or-config-hint>
            </div>
            <or-report-select [id]="'priorityone-' + block.id"
                              [options]="rankOneEndOptions"
                              [value]="rankOneEnd"
                              (selectedChange)="patch.emit({ rankOneEnd: $any($event) })">
            </or-report-select>
          }

          <!-- 100 minus the brands: the share who associate the attribute with nobody. -->
          <div class="mt-2">
            <input type="checkbox"
                   [id]="'whitespace-' + block.id"
                   [checked]="showWhiteSpace"
                   (change)="patch.emit({ showWhiteSpace: $any($event.target).checked })">
            <label class="grey4 nav-font12 mb-0 ml-1" [attr.for]="'whitespace-' + block.id">
              Show what nobody owns
            </label>
          </div>
        }

        @if (showsLeftAnswer) {
          <div class="mt-2">
            <input type="checkbox"
                   [id]="'showgap-' + block.id"
                   [checked]="showGap"
                   (change)="patch.emit({ showGap: $any($event.target).checked })">
            <label class="grey4 nav-font12 mb-0 ml-1" [attr.for]="'showgap-' + block.id">
              Show the margin between the two sides
            </label>
          </div>
        }

        @if (showsTableStats) {
          <!-- The tests' confidence, thresholds and weighting are the report's, not this table's:
               said here, with the way to them, so nobody hunts for them in a table's settings. -->
          <button type="button" class="report-stats-chip w-100 mt-3" aria-haspopup="dialog" (click)="openStatistics.emit()">
            <i class="fas fa-calculator mr-2" aria-hidden="true"></i>
            <span class="text-left flex-fill">
              <span class="d-block font-weight-bold">Report statistics</span>
              <span class="d-block report-stats-scope">Set for the whole report, not just this table</span>
              <span class="d-block">{{ statisticsLine }}</span>
            </span>
            <span class="report-stats-edit ml-2">Edit</span>
          </button>
          <div class="d-flex align-items-center mt-3 mb-1">
            <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'sig-' + block.id">
              Significance testing
            </label>
            <or-config-hint class="ml-1"
                            text="Marks differences too large to be chance. Compare columns gives each column a letter and marks a figure with the letters of the columns, from the same question, it beats. Compare with everyone else marks a figure above or below the rest of the sample.">
            </or-config-hint>
          </div>
          <or-report-select [id]="'sig-' + block.id"
                            [options]="sigTestOptions"
                            [value]="sigTest"
                            (selectedChange)="patch.emit({ sigTest: $any($event) })">
          </or-report-select>

          <div class="d-flex align-items-center mt-3 mb-1">
            <span class="grey5 nav-font12 font-weight-bold">Nets</span>
            <or-config-hint class="ml-1"
                            text="Summary rows that combine answers -- a top-two box, or any of several brands. Each respondent counts once, however many of its answers they gave.">
            </or-config-hint>
          </div>
          @for (net of nets; track $index; let i = $index) {
            <div class="filter-card mb-2">
              <div class="d-flex align-items-center mb-1">
                <input type="text"
                       class="form-control form-control-sm flex-fill"
                       [attr.aria-label]="'Name of net ' + (i + 1)"
                       [value]="net.label"
                       (change)="renameNet(i, $any($event.target).value)">
                <button type="button"
                        class="editor-action editor-remove btn btn-sm p-0 ml-2"
                        [attr.aria-label]="'Remove net ' + net.label"
                        (click)="removeNet(i)">
                  <i class="fas fa-close" aria-hidden="true"></i>
                </button>
              </div>
              <div class="chip-list" role="group" [attr.aria-label]="'Answers in ' + net.label">
                @for (answer of rowAnswers; track answer) {
                  <label class="answer-chip" [class.is-on]="net.answers.includes(answer)">
                    <input type="checkbox"
                           class="sr-only"
                           [checked]="net.answers.includes(answer)"
                           (change)="toggleNetAnswer(i, answer, $any($event.target).checked)">
                    {{ answer }}
                  </label>
                }
              </div>
              @if (!net.answers.length) {
                <p class="grey4 nav-font12 mt-1 mb-0">Pick the answers this net combines.</p>
              }
            </div>
          }
          <div class="d-flex flex-wrap align-items-center">
            <button type="button" class="btn btn-sm btn-link px-0 shadow-none nav-font12 mr-3" (click)="addNet()">
              <i class="fas fa-plus mr-1" aria-hidden="true"></i>Add a net
            </button>
            @for (preset of netPresets; track preset.label) {
              <button type="button" class="preset-chip btn btn-sm shadow-none nav-font12 mr-1 mb-1"
                      [title]="preset.label"
                      (click)="addNet(preset)">+ {{ preset.label }}</button>
            }
          </div>

          @if (takesMean) {
            <div class="d-flex align-items-center mt-3">
              <input type="checkbox"
                     [id]="'mean-' + block.id"
                     [checked]="showMean"
                     (change)="setShowMean($any($event.target).checked)">
              <label class="grey5 nav-font12 mb-0 ml-1" [attr.for]="'mean-' + block.id">
                Add a mean score
              </label>
              <or-config-hint class="ml-1"
                              text="Scores each answer by its code. Leave out answers that are not on the scale, such as Don't know.">
              </or-config-hint>
            </div>
            @if (showMean) {
              <p class="grey4 nav-font12 mt-1 mb-1">Leave out of the mean:</p>
              <div class="chip-list" role="group" aria-label="Answers left out of the mean">
                @for (answer of rowAnswers; track answer) {
                  <label class="answer-chip is-exclude" [class.is-on]="meanExcludes.includes(answer)">
                    <input type="checkbox"
                           class="sr-only"
                           [checked]="meanExcludes.includes(answer)"
                           (change)="toggleMeanExclude(answer, $any($event.target).checked)">
                    {{ answer }}
                  </label>
                }
              </div>
            }
          }
        }

        @if (showsValueUnit) {
          <div class="d-flex align-items-center mt-2 mb-1">
            <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'unit-' + block.id">
              These figures are
            </label>
            <or-config-hint class="ml-1"
                            text="Choose a percentage only when the items are shares that add up to 100.">
            </or-config-hint>
          </div>
          <or-report-select [id]="'unit-' + block.id"
                            [options]="valueUnitOptions"
                            [value]="valueUnit"
                            (selectedChange)="patch.emit({ valueUnit: $any($event) })">
          </or-report-select>
        }

        <!-- A question has several answers and a single figure has room for one, so this is where
             that is decided. The line underneath prints what it settled on, because the fallback --
             the first answer as asked -- is right for a yes/no and wrong for a scale asked
             backwards. -->
        @if (showsCountedAnswers) {
          <div class="d-flex align-items-baseline mt-2 mb-1">
            <span class="grey5 nav-font12 font-weight-bold">Answers counted</span>
            <or-config-hint class="ml-1"
                            text="The answers summed to make the figure. One for a yes/no, two for the usual top-two-box read off a five-point scale.">
            </or-config-hint>
          </div>
          <ul class="item-list list-unstyled mb-0">
            @for (answer of answerChoices; track answer) {
              <li class="d-flex align-items-start">
                <input type="checkbox"
                       class="mt-1"
                       [id]="'answer-' + block.id + '-' + answer"
                       [checked]="isAnswerCounted(answer)"
                       (change)="toggleAnswer(answer, $any($event.target).checked)">
                <label class="grey5 nav-font12 mb-0 ml-2" [attr.for]="'answer-' + block.id + '-' + answer">
                  {{ answer }}
                </label>
              </li>
            }
          </ul>
          <p class="grey4 nav-font12 mt-1 mb-0">{{ countedAnswerLabel() }}</p>
        }

        @if (showsDropOff) {
          <div class="mt-2">
            <input type="checkbox"
                   [id]="'dropoff-' + block.id"
                   [checked]="dropOffOn"
                   (change)="patch.emit({ showDropOff: $any($event.target).checked })">
            <label class="grey5 nav-font12 mb-0 ml-1" [attr.for]="'dropoff-' + block.id">
              Show the drop-off between stages
            </label>
            <or-config-hint class="ml-1"
                            text="The fall from the stage above, as a share of it — 60% to 30% is a 50% drop-off, not a 30-point one.">
            </or-config-hint>
          </div>
        }

        @if (showsNet) {
          <!-- What is scored, and which end is good, is set by the answer bands above -- the same bands
               the dial and the bar chart use. These are only what gets shown. -->
          <!-- One bar headlines its scores, so it can show both; a battery has one column down its
               right for them, so it chooses. -->
          @if (singleBar) {
            <div class="mt-2">
              <input type="checkbox"
                     [id]="'net-' + block.id"
                     [checked]="netOn"
                     (change)="patch.emit({ showNet: $any($event.target).checked })">
              <label class="grey5 nav-font12 mb-0 ml-1" [attr.for]="'net-' + block.id">
                Show the Net score
              </label>
            </div>
            <div class="mt-1">
              <input type="checkbox"
                     [id]="'share-' + block.id"
                     [checked]="shareOn"
                     (change)="patch.emit({ showShare: $any($event.target).checked })">
              <label class="grey5 nav-font12 mb-0 ml-1" [attr.for]="'share-' + block.id">
                Show one band's share
              </label>
            </div>
          } @else {
            <div class="d-flex align-items-center mt-2 mb-1">
              <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'rightcol-' + block.id">
                Column on the right
              </label>
              <or-config-hint class="ml-1"
                              text="A net is the top band minus the bottom, in a red or green chip. A share is how many are in one band -- 78% weekly -- as a plain figure.">
              </or-config-hint>
            </div>
            <or-report-select [id]="'rightcol-' + block.id"
                              [options]="rightColumnOptions"
                              [value]="rightColumn"
                              (selectedChange)="setRightColumn($any($event))">
            </or-report-select>
          }

          @if (shareOn) {
              @if (shareBandOptions.length) {
                <label class="grey5 nav-font12 font-weight-bold mt-2 mb-1" [attr.for]="'stackshareband-' + block.id">
                  Which band
                </label>
                <or-report-select [id]="'stackshareband-' + block.id"
                                  [options]="shareBandOptions"
                                  [value]="shareBand"
                                  (selectedChange)="patch.emit({ shareBand: +$any($event) })">
                </or-report-select>
              }
          }

          @if (legacyBoxesNote; as note) {
            <p class="grey4 nav-font10 mb-0 mt-2">{{ note }}</p>
          }

          @if (netOn) {
            <div class="d-flex align-items-center mt-2 mb-1">
              <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'netgood-' + block.id">
                Green at or above
              </label>
              <or-config-hint class="ml-1" text="Nets at or above this are green, the rest red.">
              </or-config-hint>
            </div>
            <input type="number"
                   class="form-control form-control-sm w-100"
                   [id]="'netgood-' + block.id"
                   placeholder="50"
                   [value]="netThreshold"
                   (input)="setNetThreshold($any($event.target).value)">
          }
        }
      </div>
    </section>
  }

  <section class="config-group">
    <button type="button"
            class="config-group-head"
            [attr.aria-expanded]="isOpen('layout')"
            [attr.aria-controls]="'cfg-layout-' + block.id"
            (click)="toggleGroup('layout')">
      <span class="config-group-text">
        <span class="config-group-name">{{ groupName('layout') }}</span>
        @if (!isOpen('layout') && summaries.layout) {
          <span class="config-group-summary">{{ summaries.layout }}</span>
        }
      </span>
      <i class="fas fa-chevron-down config-group-chevron" [class.is-open]="isOpen('layout')" aria-hidden="true"></i>
    </button>
    <div class="config-group-body" [id]="'cfg-layout-' + block.id" [hidden]="!isOpen('layout')">
      <label class="grey5 nav-font12 font-weight-bold mt-2 mb-1" [attr.for]="'width-' + block.id">
        Width
      </label>
      <or-report-select [id]="'width-' + block.id"
                        [options]="widthOptions"
                        [value]="width"
                        (selectedChange)="setWidth($event)">
      </or-report-select>
      @if (width === 'half') {
        <p class="grey5 nav-font12 mt-1 mb-0">Drag the card by its grip to put it on the other side.</p>
      }

      @if (orientations.length) {
        <label class="grey5 nav-font12 font-weight-bold mt-2 mb-1" [attr.for]="'orient-' + block.id">
          Orientation
        </label>
        <or-report-select [id]="'orient-' + block.id"
                          [options]="orientationOptions"
                          [value]="orientation"
                          (selectedChange)="patch.emit({ orientation: $any($event) })">
        </or-report-select>
      }

      @if (isDial) {
        <div class="d-flex align-items-center mt-2 mb-1">
          <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'display-' + block.id">
            Drawn as
          </label>
          <or-config-hint class="ml-1"
                          text="The figure alone suits a net under a chart that has already shown the same answers — the ring would divide them a second time.">
          </or-config-hint>
        </div>
        <or-report-select [id]="'display-' + block.id"
                          [options]="displayOptions"
                          [value]="display"
                          (selectedChange)="patch.emit({ display: $any($event) })">
        </or-report-select>

        <!-- Off by default: a score that shows what it is made of can be checked, and one that
             does not cannot. -->
        <div class="mt-2">
          <input type="checkbox"
                 [id]="'dialnote-' + block.id"
                 [checked]="hideLabels"
                 (change)="patch.emit({ hideLabels: $any($event.target).checked })">
          <label class="grey4 nav-font12 mb-0 ml-1" [attr.for]="'dialnote-' + block.id">
            Hide the line explaining the score
          </label>
        </div>
      }

      @if (showsLeftAnswer) {
        <div class="d-flex align-items-center mt-2 mb-1">
          <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'leftpole-' + block.id">
            Left of the bar
          </label>
          <or-config-hint class="ml-1" [text]="leftAnswerHint"></or-config-hint>
        </div>
        <or-report-select [id]="'leftpole-' + block.id"
                          [options]="leftAnswerOptions"
                          [value]="leftAnswer"
                          (selectedChange)="patch.emit({ leftAnswer: $any($event) })">
        </or-report-select>
      }

      @if (showsBarOrder) {
        <div class="d-flex align-items-center mt-2 mb-1">
          <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'barorder-' + block.id">
            Bar order
          </label>
          <or-config-hint class="ml-1"
                          text="Ranks the bars by net score, using the same answers at each end as the Net column. The answers inside a bar keep the order they were asked in.">
          </or-config-hint>
        </div>
        <or-report-select [id]="'barorder-' + block.id"
                          [options]="barOrderOptions"
                          [value]="barOrder"
                          (selectedChange)="patch.emit({ barOrder: $any($event) })">
        </or-report-select>
      }

      <!-- A table is the only module with a cell to fill: every chart's mark already says whether
           it is showing a share or a count. -->
      @if (showsCellValue) {
        <label class="grey5 nav-font12 font-weight-bold mt-2 mb-1" [attr.for]="'cell-' + block.id">
          Each cell shows
        </label>
        <or-report-select [id]="'cell-' + block.id"
                          [options]="cellValueOptions"
                          [value]="cellValue"
                          (selectedChange)="patch.emit({ cellValue: $any($event) })">
        </or-report-select>

        @if (showsCountToggle) {
          <div class="mt-1">
            <input type="checkbox"
                   [id]="'counts-' + block.id"
                   [checked]="showCounts"
                   (change)="patch.emit({ showCounts: $any($event.target).checked })">
            <label class="grey4 nav-font12 mb-0 ml-1" [attr.for]="'counts-' + block.id">
              Print the number of respondents under each percentage
            </label>
          </div>
        }
      }

      @if (showsTableStats) {
        <label class="grey5 nav-font12 font-weight-bold mt-2 mb-1" [attr.for]="'shading-' + block.id">
          Cell shading
        </label>
        <or-report-select [id]="'shading-' + block.id"
                          [options]="shadingOptions"
                          [value]="shading"
                          (selectedChange)="patch.emit({ shading: $any($event) })">
        </or-report-select>
      }

      @if (showsSorting) {
        <div class="mt-2">
          <input type="checkbox"
                 [id]="'sort-' + block.id"
                 [checked]="sortDescending"
                 (change)="patch.emit({ sortDescending: $any($event.target).checked })">
          <label class="grey5 nav-font12 mb-0 ml-1" [attr.for]="'sort-' + block.id">
            Sort by size, largest first
          </label>
        </div>
      }

      @if (showsTopN) {
        <div class="d-flex align-items-center mt-2 mb-1">
          <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'topn-' + block.id">
            Show top
          </label>
          <or-config-hint class="ml-1" text="Leave blank to show every option."></or-config-hint>
        </div>
        <input type="number"
               class="form-control form-control-sm w-100"
               min="0"
               [id]="'topn-' + block.id"
               placeholder="All options"
               [value]="topN"
               (input)="setTopN($any($event.target).value)">
      }

      @if (takesMatrix) {
        <label class="grey5 nav-font12 font-weight-bold mt-2 mb-1"
               [attr.for]="'roworder-' + block.id">
          Row order
        </label>
        <or-report-select [id]="'roworder-' + block.id"
                          [options]="rowOrderOptions"
                          [value]="rowOrder"
                          (selectedChange)="patch.emit({ rowOrder: $any($event) })">
        </or-report-select>
      }

      <!-- Themes over the rows: named first, then each row put in one. Nothing in a datamap says
           which attributes belong together, so this is entirely authored. -->
      @if (showsCategories) {
        <div class="d-flex align-items-center mt-3 mb-1">
          <label class="grey5 nav-font12 font-weight-bold mb-0">Categories</label>
          <or-config-hint class="ml-1"
                          text="Groups of rows that belong together — Pricing &amp; Process, Trust &amp; Credibility. They colour the bars and get a key under the chart. Sorting scatters a category down the list, which is what makes naming it worth doing.">
          </or-config-hint>
        </div>

        @for (category of categories; track $index) {
          <div class="d-flex align-items-center" [class.mt-1]="!$first">
            <input type="text"
                   class="form-control form-control-sm"
                   maxlength="40"
                   [attr.aria-label]="'Name of category ' + ($index + 1)"
                   placeholder="Category name"
                   [value]="category.label"
                   (input)="setCategoryLabel($index, $any($event.target).value)">
            <button type="button"
                    class="btn btn-sm btn-link text-danger px-1 shadow-none"
                    [attr.aria-label]="'Remove category ' + ($index + 1)"
                    (click)="removeCategory($index)">
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>
        }

        <button type="button"
                class="btn btn-sm btn-link px-0 shadow-none nav-font12"
                (click)="addCategory()">
          <i class="fas fa-plus mr-1" aria-hidden="true"></i>Add a category
        </button>

        @if (categories.length) {
          <label class="grey5 nav-font12 font-weight-bold mt-2 mb-1">Which category each row is in</label>
          @for (row of categoryRows; track row.key; let i = $index) {
            <div class="mb-2">
              <span class="grey4 nav-font12 d-block"
                    [id]="'rowcat-' + block.id + '-' + i">{{ row.label }}</span>
              <or-report-select [id]="'rowcatsel-' + block.id + '-' + i"
                                [options]="categoryOptions"
                                [value]="categoryOf(row.key)"
                                [labelledBy]="'rowcat-' + block.id + '-' + i"
                                (selectedChange)="setRowCategory(row.key, $any($event))">
              </or-report-select>
            </div>
          }
        }
      }

      @if (showsHideLabels) {
        <div class="mt-2">
          <input type="checkbox"
                 [id]="'labels-' + block.id"
                 [checked]="hideLabels"
                 (change)="patch.emit({ hideLabels: $any($event.target).checked })">
          <label class="grey5 nav-font12 mb-0 ml-1" [attr.for]="'labels-' + block.id">
            Hide answer labels
          </label>
          <or-config-hint class="ml-1"
                          text="Gives the chart the space the labels took. They still show on hover.">
          </or-config-hint>
        </div>
      }

      @if (showsEmptyAnswers) {
        <div class="mt-2">
          <input type="checkbox"
                 [id]="'empty-' + block.id"
                 [checked]="showEmptyAnswers"
                 (change)="patch.emit({ hideEmptyAnswers: !$any($event.target).checked })">
          <label class="grey5 nav-font12 mb-0 ml-1" [attr.for]="'empty-' + block.id">
            Show answers nobody picked
          </label>
          <or-config-hint class="ml-1"
                          text="Drawn as 0%, so the scale has no gaps. Hiding them only changes the drawing — bands still count them.">
          </or-config-hint>
        </div>
      }

      @if (takesAxes) {
        <!-- Drawn at the average of the items on the chart, so the quadrants read as "above average
             on both" rather than as an arbitrary middle. -->
        <div class="mt-2">
          <input type="checkbox"
                 [id]="'quadrants-' + block.id"
                 [checked]="showQuadrants"
                 (change)="patch.emit({ showQuadrants: $any($event.target).checked })">
          <label class="grey4 nav-font12 mb-0 ml-1" [attr.for]="'quadrants-' + block.id">
            Show average lines
          </label>
        </div>
      }

      @if (showsVerbatimList) {
        <div class="d-flex align-items-center mt-2 mb-1">
          <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'pagesize-' + block.id">
            Answers per page
          </label>
          <or-config-hint class="ml-1"
                          text="How many answers on a page, with Previous and Next under them. Blank uses 15.">
          </or-config-hint>
        </div>
        <input type="number"
               class="form-control form-control-sm w-100"
               min="1"
               [id]="'pagesize-' + block.id"
               placeholder="15"
               [value]="pageSize"
               (change)="setPageSize($any($event.target).value)">

        <div class="mt-2">
          <input type="checkbox"
                 [id]="'searchable-' + block.id"
                 [checked]="searchable"
                 (change)="patch.emit({ searchable: $any($event.target).checked })">
          <label class="grey4 nav-font12 mb-0 ml-1" [attr.for]="'searchable-' + block.id">
            Let readers search these answers
          </label>
        </div>
      }

      @if (showsWordCloud) {
        <div class="d-flex align-items-center mt-2 mb-1">
          <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'maxwords-' + block.id">
            Words shown
          </label>
          <or-config-hint class="ml-1"
                          text="How many words the cloud draws. The one setting that affects how long it takes to lay out. Blank uses 60.">
          </or-config-hint>
        </div>
        <input type="number"
               class="form-control form-control-sm w-100"
               min="1"
               [id]="'maxwords-' + block.id"
               placeholder="60"
               [value]="maxWords"
               (change)="setMaxWords($any($event.target).value)">
      }

      @if (showsExplainerPlacement) {
        <div class="d-flex align-items-center mt-3 mb-1">
          <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'explainer-' + block.id">
            Explainer
          </label>
          <or-config-hint class="ml-1"
                          text="A panel teaching the stages and rungs set up here, on its own page. It reads them from this matrix, so the two cannot disagree — only the notes under each part are written on it.">
          </or-config-hint>
        </div>
        <or-report-select [id]="'explainer-' + block.id"
                          [options]="explainerPlacementOptions"
                          [value]="explainerPlacement"
                          (selectedChange)="explainerPlacementChange.emit($any($event))">
        </or-report-select>
      }
    </div>
  </section>

  @if (hasText) {
    <section class="config-group">
      <button type="button"
              class="config-group-head"
              [attr.aria-expanded]="isOpen('text')"
              [attr.aria-controls]="'cfg-text-' + block.id"
              (click)="toggleGroup('text')">
        <span class="config-group-text">
          <span class="config-group-name">{{ groupName('text') }}</span>
          @if (!isOpen('text') && summaries.text) {
            <span class="config-group-summary">{{ summaries.text }}</span>
          }
        </span>
        <i class="fas fa-chevron-down config-group-chevron" [class.is-open]="isOpen('text')" aria-hidden="true"></i>
      </button>
      <div class="config-group-body" [id]="'cfg-text-' + block.id" [hidden]="!isOpen('text')">
        @if (takesAxes) {
          <div class="d-flex align-items-center mt-3 mb-1">
            <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'xlabel-' + block.id">
              Axis titles
            </label>
            <or-config-hint class="ml-1"
                            text="What each axis measures, in your own words. Blank uses the question's own wording.">
            </or-config-hint>
          </div>
          <input type="text"
                 class="form-control form-control-sm w-100"
                 maxlength="60"
                 [id]="'xlabel-' + block.id"
                 [placeholder]="axisPlaceholders[0]"
                 [value]="xLabel"
                 (input)="patch.emit({ xLabel: $any($event.target).value })">
          <input type="text"
                 class="form-control form-control-sm w-100 mt-1"
                 maxlength="60"
                 [attr.aria-label]="'Vertical axis title'"
                 [placeholder]="axisPlaceholders[1]"
                 [value]="yLabel"
                 (input)="patch.emit({ yLabel: $any($event.target).value })">

          @if (showQuadrants) {
            <input type="text"
                   class="form-control form-control-sm w-100 mt-1"
                   maxlength="60"
                   [attr.aria-label]="'Caption for the top-right quadrant'"
                   placeholder="Caption for the top-right corner"
                   [value]="quadrantLabel"
                   (input)="patch.emit({ quadrantLabel: $any($event.target).value })">
          }
        }

        @if (takesStages) {
          <div class="d-flex align-items-center mt-2 mb-1">
            <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'totallabel-' + block.id">
              Leading column
            </label>
            <or-config-hint class="ml-1"
                            text="A column before the stages adding up the answers it counts -- Total Aided Awareness is everyone but Unaware. Blank draws no such column.">
            </or-config-hint>
          </div>
          <input type="text"
                 class="form-control form-control-sm w-100"
                 maxlength="30"
                 [id]="'totallabel-' + block.id"
                 placeholder="e.g. Total Aided Awareness"
                 [value]="stageTotalLabel"
                 (input)="patch.emit({ totalLabel: $any($event.target).value })">
          <p class="grey5 nav-font12 mt-2 mb-0">
            Column names and the band's captions are typed straight onto the card.
          </p>
        }

        <!-- Only where there are no stages or panels for the band to sit under: with them, its
             captions are typed onto the card, one under each. -->
        @if (showsSpectrum) {
          <div class="d-flex align-items-center mt-3 mb-1">
            <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'spectrum-' + block.id">
              Direction band
            </label>
            <or-config-hint class="ml-1"
                            text="Captions under the chart, left to right, naming what movement across it means. One per line. Leave it empty for no band.">
            </or-config-hint>
          </div>
          <textarea class="form-control form-control-sm w-100"
                    rows="4"
                    [id]="'spectrum-' + block.id"
                    placeholder="One caption per line"
                    [value]="spectrumText"
                    (input)="setSpectrum($any($event.target).value)"></textarea>
        }

        @if (isDial) {
          @if (showsNetWord) {
            <div class="d-flex align-items-center mt-2 mb-1">
              <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'netword-' + block.id">
                Net of
              </label>
              <or-config-hint class="ml-1"
                              text="One word — Momentum, Agree, Advocacy. The card heads itself “Net Momentum” and reads “Positive momentum” or “Negative momentum” under the figure, from the sign of the score.">
              </or-config-hint>
            </div>
            <input type="text"
                   class="form-control form-control-sm w-100"
                   maxlength="30"
                   [id]="'netword-' + block.id"
                   placeholder="Momentum"
                   [value]="netLabel"
                   (input)="patch.emit({ netLabel: $any($event.target).value })">
          }
        }

        @if (showsNet) {
          <!-- A single bar's caption is written on the card, in its headline. A battery's is drawn
               inside the chart beside every bar, where there is nowhere to type it. -->
          @if (shareOn && !singleBar) {
            <div class="d-flex align-items-center mt-2 mb-1">
              <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'sharecaption-' + block.id">
                Share caption
              </label>
              <or-config-hint class="ml-1"
                              [text]="shareCaptionHint">
              </or-config-hint>
            </div>
            <input type="text"
                   class="form-control form-control-sm w-100"
                   maxlength="120"
                   [id]="'sharecaption-' + block.id"
                   [placeholder]="shareCaptionPlaceholder"
                   [value]="shareCaption"
                   (input)="patch.emit({ shareCaption: $any($event.target).value })">
          }

          @if (netOn) {
            <div class="d-flex align-items-center mt-2 mb-1">
              <label class="grey5 nav-font12 font-weight-bold mb-0" [attr.for]="'netlabel-' + block.id">
                Net of
              </label>
              <or-config-hint class="ml-1"
                              text='Reads as "Net Agree". Leave blank for just "Net".'>
              </or-config-hint>
            </div>
            <input type="text"
                   class="form-control form-control-sm w-100"
                   maxlength="40"
                   [id]="'netlabel-' + block.id"
                   placeholder="Agree"
                   [value]="netLabel"
                   (input)="patch.emit({ netLabel: $any($event.target).value })">
          }
        }
      </div>
    </section>
  }

  @if (advice; as text) {
    <p class="grey4 nav-font12 mx-3 my-3">
      <i class="fas fa-circle-info mr-1" aria-hidden="true"></i>{{ text }}
    </p>
  }
</div>

<!-- No Done: the changes are the deck's until it is saved, and a button here that looked like
     finishing would read as though they were kept. Close, above, only closes the panel. Remove and
     Add module are the deck's, in the foot it keeps under this. -->
